import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Volume2, 
  VolumeX, 
  Play, 
  Settings, 
  Check, 
  X,
  Headphones,
  Bell
} from 'lucide-react';
import { soundNotificationService, SoundType } from '@/services/soundNotificationService';
import { Button } from '@/components/ui/Button';
import { toast } from 'react-hot-toast';

interface SoundNotificationSettingsProps {
  onClose: () => void;
}

const SoundNotificationSettings: React.FC<SoundNotificationSettingsProps> = ({ onClose }) => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [volume, setVolume] = useState(0.3);
  const [isPlaying, setIsPlaying] = useState<SoundType | null>(null);

  useEffect(() => {
    // Load current settings
    setIsEnabled(soundNotificationService.isAudioEnabled());
    setVolume(soundNotificationService.getVolume());
  }, []);

  const handleToggleEnabled = () => {
    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);
    soundNotificationService.setEnabled(newEnabled);
    
    if (newEnabled) {
      toast.success('Sound notifications enabled');
      soundNotificationService.playSound(SoundType.Success);
    } else {
      toast('Sound notifications disabled', { icon: '🔇' });
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    soundNotificationService.setVolume(newVolume);
  };

  const handleTestSound = async (soundType: SoundType) => {
    if (!isEnabled) {
      toast.error('Enable sound notifications first');
      return;
    }

    setIsPlaying(soundType);
    await soundNotificationService.playSound(soundType);
    setTimeout(() => setIsPlaying(null), 500);
  };

  const handleTestSequence = async () => {
    if (!isEnabled) {
      toast.error('Enable sound notifications first');
      return;
    }

    setIsPlaying(SoundType.Default);
    await soundNotificationService.playWelcomeSound();
    setTimeout(() => setIsPlaying(null), 1000);
  };

  const soundTypes = [
    { type: SoundType.Default, label: 'Default', description: 'General notifications' },
    { type: SoundType.Success, label: 'Success', description: 'Completed actions' },
    { type: SoundType.Warning, label: 'Warning', description: 'Important alerts' },
    { type: SoundType.Error, label: 'Error', description: 'Error notifications' },
    { type: SoundType.Message, label: 'Message', description: 'Chat messages' },
    { type: SoundType.Task, label: 'Task', description: 'Task assignments' },
    { type: SoundType.Calendar, label: 'Calendar', description: 'Event reminders' },
    { type: SoundType.Template, label: 'Template', description: 'Template reviews' },
    { type: SoundType.Inventory, label: 'Inventory', description: 'Inventory updates' },
    { type: SoundType.Commission, label: 'Commission', description: 'Commission alerts' },
    { type: SoundType.Chime, label: 'Chime', description: 'Gentle notification' },
    { type: SoundType.Bell, label: 'Bell', description: 'Classic bell sound' },
    { type: SoundType.Ding, label: 'Ding', description: 'Quick notification' },
    { type: SoundType.Pop, label: 'Pop', description: 'Sharp notification' },
    { type: SoundType.Marimba, label: 'Marimba', description: 'Warm wooden mallet roll' },
    { type: SoundType.Harp, label: 'Harp', description: 'Ascending harp arpeggio' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-slate-900 rounded-2xl border border-white/10 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-blue-500/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Sound Notifications</h2>
              <p className="text-white/60">Configure notification sounds and volume</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Main Controls */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isEnabled ? (
                <Volume2 className="w-5 h-5 text-green-400" />
              ) : (
                <VolumeX className="w-5 h-5 text-red-400" />
              )}
              <div>
                <h3 className="text-white font-medium">Sound Notifications</h3>
                <p className="text-white/60 text-sm">
                  {isEnabled ? 'Sounds are enabled' : 'Sounds are disabled'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-green-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Volume Control */}
          {isEnabled && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Volume: {Math.round(volume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${volume * 100}%, rgba(255,255,255,0.1) ${volume * 100}%, rgba(255,255,255,0.1) 100%)`
                }}
              />
            </div>
          )}
        </div>

        {/* Test Sounds */}
        {isEnabled && (
          <>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-medium mb-3">Test Sounds</h3>
              <div className="flex gap-2 mb-3">
                <Button
                  onClick={handleTestSequence}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                  disabled={isPlaying !== null}
                >
                  <Play className="w-4 h-4" />
                  Test Welcome Sequence
                </Button>
                <Button
                  onClick={() => soundNotificationService.testAllSounds()}
                  variant="outline"
                  className="text-white border-white/20 hover:bg-white/10 flex items-center gap-2"
                  disabled={isPlaying !== null}
                >
                  <Settings className="w-4 h-4" />
                  Test All Sounds
                </Button>
              </div>
            </div>

            {/* Individual Sound Types */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-medium mb-3">Individual Sound Types</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {soundTypes.map(({ type, label, description }) => (
                  <button
                    key={type}
                    onClick={() => handleTestSound(type)}
                    disabled={isPlaying !== null}
                    className={`p-3 rounded-lg border transition-all text-left ${
                      isPlaying === type
                        ? 'bg-blue-500/20 border-blue-400/50'
                        : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium text-sm">{label}</div>
                        <div className="text-white/60 text-xs">{description}</div>
                      </div>
                      {isPlaying === type ? (
                        <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 text-white/40" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Audio Info */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-blue-400" />
            <h4 className="text-white font-medium text-sm">Audio Information</h4>
          </div>
          <div className="text-white/60 text-xs space-y-1">
            <p>• Sounds are generated using Web Audio API</p>
            <p>• Settings are saved locally in your browser</p>
            <p>• Different notification types have unique sounds</p>
            <p>• Sounds will only play after user interaction with the page</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-white/10 bg-white/5">
        <div className="flex items-center justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="text-white border-white/20 hover:bg-white/10"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              toast.success('Sound settings saved!');
              soundNotificationService.playSound(SoundType.Success);
            }}
            className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Save Settings
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default SoundNotificationSettings;
